import { LIVECHAT_PUBLIC_ORIGIN } from '@/lib/livechat-public-origin';
// ============================================================
// RBAC (Role-Based Access Control) - Kustomer Suite
// Based on PRD Section 5: Role-Based Access Control
// ============================================================

export type UserRole = 'owner' | 'admin' | 'marketing' | 'cs_agent' | 'finance' | 'developer';

export type Permission =
  // Dashboard
  | 'dashboard.view'
  | 'dashboard.view_financial'
  | 'dashboard.view_technical'
  // Inbox
  | 'inbox.view'
  | 'inbox.respond'
  | 'inbox.assign'
  | 'inbox.edit_message'
  | 'inbox.delete_message'
  | 'inbox.internal_notes'
  // Campaigns
  | 'campaigns.view'
  | 'campaigns.create'
  | 'campaigns.edit'
  | 'campaigns.delete'
  // Analytics
  | 'analytics.view'
  | 'analytics.view_cost'
  | 'analytics.export'
  // Contacts
  | 'contacts.view'
  | 'contacts.view_assigned'
  | 'contacts.create'
  | 'contacts.edit'
  | 'contacts.delete'
  | 'contacts.import'
  | 'contacts.export'
  // Segments
  | 'segments.view'
  | 'segments.create'
  | 'segments.edit'
  | 'segments.delete'
  // AI Chatbot
  | 'ai_chatbot.view'
  | 'ai_chatbot.configure'
  // Knowledge Base
  | 'knowledge_base.view'
  | 'knowledge_base.crud'
  // Intent Management
  | 'intent_management.view'
  | 'intent_management.crud'
  // Automation
  | 'automation.view'
  | 'automation.crud'
  // Support Tickets
  | 'support_tickets.view'
  | 'support_tickets.respond'
  | 'support_tickets.assign'
  // Live Chat Widget
  | 'live_chat.view'
  | 'live_chat.configure'
  // Templates
  | 'templates.view'
  | 'templates.crud'
  // Billing
  | 'billing.view'
  | 'billing.topup'
  | 'billing.transfer'
  | 'billing.set_budget'
  // Settings
  | 'settings.view'
  | 'settings.edit'
  /** Invite/revoke members & change roles — granted to Owner & Admin only (see stores.UserCanManageTeam) */
  | 'settings.team_manage'
  | 'settings.channels'
  | 'settings.api_keys'
  | 'settings.integrations'
  // Profile
  | 'profile.view'
  | 'profile.edit'
  // Help
  | 'help.view'
  // Audit Log
  | 'audit_log.view'
  | 'audit_log.export'
  | 'audit_log.view_technical'
  // GDPR
  | 'gdpr.export_data'
  | 'gdpr.delete_data'
  // Account
  | 'account.delete'
  | 'account.transfer_ownership'
  // Store
  | 'store.global_view'
  | 'store.manage';

// ============================================================
// Role Permissions Map
// ============================================================

const ALL_PERMISSIONS: Permission[] = [
  'dashboard.view', 'dashboard.view_financial', 'dashboard.view_technical',
  'inbox.view', 'inbox.respond', 'inbox.assign', 'inbox.edit_message', 'inbox.delete_message', 'inbox.internal_notes',
  'campaigns.view', 'campaigns.create', 'campaigns.edit', 'campaigns.delete',
  'analytics.view', 'analytics.view_cost', 'analytics.export',
  'contacts.view', 'contacts.view_assigned', 'contacts.create', 'contacts.edit', 'contacts.delete', 'contacts.import', 'contacts.export',
  'segments.view', 'segments.create', 'segments.edit', 'segments.delete',
  'ai_chatbot.view', 'ai_chatbot.configure',
  'knowledge_base.view', 'knowledge_base.crud',
  'intent_management.view', 'intent_management.crud',
  'automation.view', 'automation.crud',
  'support_tickets.view', 'support_tickets.respond', 'support_tickets.assign',
  'live_chat.view', 'live_chat.configure',
  'templates.view', 'templates.crud',
  'billing.view', 'billing.topup', 'billing.transfer', 'billing.set_budget',
  'settings.view', 'settings.edit', 'settings.team_manage', 'settings.channels', 'settings.api_keys', 'settings.integrations',
  'profile.view', 'profile.edit',
  'help.view',
  'audit_log.view', 'audit_log.export', 'audit_log.view_technical',
  'gdpr.export_data', 'gdpr.delete_data',
  'account.delete', 'account.transfer_ownership',
  'store.global_view', 'store.manage',
];

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  // Owner: 100% full access
  owner: [...ALL_PERMISSIONS],

  // Admin: ~95% - no account delete, no transfer ownership
  admin: ALL_PERMISSIONS.filter(p => p !== 'account.delete' && p !== 'account.transfer_ownership'),

  // Marketing: campaigns, contacts, analytics, segments, templates, KB, billing read-only
  marketing: [
    'dashboard.view',
    'campaigns.view', 'campaigns.create', 'campaigns.edit', 'campaigns.delete',
    'analytics.view', 'analytics.export',
    'contacts.view', 'contacts.create', 'contacts.edit', 'contacts.import', 'contacts.export',
    'segments.view', 'segments.create', 'segments.edit', 'segments.delete',
    'ai_chatbot.view',
    'knowledge_base.view', 'knowledge_base.crud',
    'templates.view', 'templates.crud',
    'billing.view',
    'profile.view', 'profile.edit',
    'help.view',
  ],

  // CS Agent: inbox, support tickets, KB, customer profiles (assigned)
  cs_agent: [
    'dashboard.view',
    'inbox.view', 'inbox.respond', 'inbox.edit_message', 'inbox.delete_message', 'inbox.internal_notes',
    'support_tickets.view', 'support_tickets.respond',
    'contacts.view', 'contacts.view_assigned', 'contacts.edit',
    'ai_chatbot.view',
    'knowledge_base.view', 'knowledge_base.crud',
    'profile.view', 'profile.edit',
    'help.view',
  ],

  // Finance: billing full, analytics cost, global store view
  finance: [
    'dashboard.view', 'dashboard.view_financial',
    'analytics.view', 'analytics.view_cost', 'analytics.export',
    'billing.view', 'billing.topup', 'billing.transfer', 'billing.set_budget',
    'profile.view', 'profile.edit',
    'help.view',
    'store.global_view',
  ],

  // Developer: settings (API keys, integrations, channels), audit log technical, AI/KB
  developer: [
    'dashboard.view', 'dashboard.view_technical',
    'ai_chatbot.view', 'ai_chatbot.configure',
    'knowledge_base.view', 'knowledge_base.crud',
    'settings.view', 'settings.api_keys', 'settings.integrations', 'settings.channels',
    'audit_log.view', 'audit_log.view_technical',
    'profile.view', 'profile.edit',
    'help.view',
  ],
};

// ============================================================
// Helper Functions
// ============================================================

export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function hasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(role, p));
}

export function hasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
  return permissions.every(p => hasPermission(role, p));
}

// ============================================================
// Navigation Configuration
// ============================================================

export interface NavItem {
  id: string;
  labelKey: string; // i18n key under 'nav'
  icon: string; // lucide icon name
  href: string;
  permission: Permission;
  badge?: string;
  badgeColor?: string;
  section: 'main' | 'bottom';
}

export const NAV_ITEMS: NavItem[] = [
  // Main section
  { id: 'dashboard', labelKey: 'nav.dashboard', icon: 'LayoutDashboard', href: '/dashboard', permission: 'dashboard.view', section: 'main' },
  { id: 'inbox', labelKey: 'nav.inbox', icon: 'MessageSquare', href: '/inbox', permission: 'inbox.view', badge: '142', badgeColor: 'bg-red-600', section: 'main' },
  { id: 'campaigns', labelKey: 'nav.campaigns', icon: 'Send', href: '/campaigns', permission: 'campaigns.view', section: 'main' },
  { id: 'analytics', labelKey: 'nav.analytics', icon: 'BarChart3', href: '/analytics', permission: 'analytics.view', section: 'main' },
  { id: 'customers', labelKey: 'nav.customers', icon: 'Users', href: '/customers', permission: 'contacts.view', section: 'main' },
  { id: 'ai-chatbot', labelKey: 'nav.aiChatbot', icon: 'Bot', href: '/ai-chatbot', permission: 'ai_chatbot.view', badge: 'New', badgeColor: 'bg-pink-600', section: 'main' },
  { id: 'automation', labelKey: 'nav.automation', icon: 'Workflow', href: '/automation', permission: 'automation.view', section: 'main' },
  { id: 'segment-builder', labelKey: 'nav.segmentBuilder', icon: 'Filter', href: '/segment-builder', permission: 'segments.view', section: 'main' },
  { id: 'support-tickets', labelKey: 'nav.supportTickets', icon: 'Ticket', href: '/support-tickets', permission: 'support_tickets.view', badge: '12', badgeColor: 'bg-red-600', section: 'main' },
  { id: 'live-chat-widget', labelKey: 'nav.liveChatWidget', icon: 'MessageCircle', href: '/live-chat-widget', permission: 'live_chat.view', section: 'main' },
  { id: 'livechat-public', labelKey: 'nav.livechat', icon: 'ExternalLink', href: `${LIVECHAT_PUBLIC_ORIGIN}/livechat`, permission: 'live_chat.view', section: 'main' },
  { id: 'templates', labelKey: 'nav.templates', icon: 'FileText', href: '/templates', permission: 'templates.view', section: 'main' },
  { id: 'billing', labelKey: 'nav.billing', icon: 'Wallet', href: '/billing', permission: 'billing.view', section: 'main' },
  // Bottom section
  { id: 'settings', labelKey: 'nav.settings', icon: 'Settings', href: '/settings', permission: 'settings.view', section: 'bottom' },
  { id: 'audit-log', labelKey: 'nav.auditLog', icon: 'ScrollText', href: '/audit-log', permission: 'audit_log.view', section: 'bottom' },
  { id: 'help', labelKey: 'nav.help', icon: 'HelpCircle', href: '/help', permission: 'help.view', section: 'bottom' },
];

export function getNavItemsForRole(role: UserRole): NavItem[] {
  return NAV_ITEMS.filter(item => hasPermission(role, item.permission));
}

export function getMainNavForRole(role: UserRole): NavItem[] {
  return getNavItemsForRole(role).filter(item => item.section === 'main');
}

export function getBottomNavForRole(role: UserRole): NavItem[] {
  return getNavItemsForRole(role).filter(item => item.section === 'bottom');
}

// ============================================================
// Role Display Info
// ============================================================

export const ROLE_INFO: Record<UserRole, { labelKey: string; color: string; bgColor: string }> = {
  owner: { labelKey: 'roles.owner', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  admin: { labelKey: 'roles.admin', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  marketing: { labelKey: 'roles.marketing', color: 'text-green-700', bgColor: 'bg-green-100' },
  cs_agent: { labelKey: 'roles.csAgent', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  finance: { labelKey: 'roles.finance', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  developer: { labelKey: 'roles.developer', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};
